import type { FC } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";

import { SWReactIcons } from "@similarweb/icons";
import { DonutChart, LEGEND_POSITIONS } from "@similarweb/pro-logic-components";
import { colorsPalettes } from "@similarweb/styles";
import { Text } from "@similarweb/ui-components/foundations";

import type { IUserKeywordGroup } from "app/services/keyword-groups";
import { keywordGroupsTransforms, useKeywordGroupById } from "app/services/keyword-groups";
import { BasicDurations } from "app/services/DurationService";
import BoxTitle from "pro-features/components/BoxTitle/src/BoxTitle";
import { translate } from "app/filters/i18nFilter";

import { DataRowTitle, DataTitle } from "../components/datarow/DataRowStyledComponents";

import { getConfig } from "./serpCompositionGraphConfig";
import {
    AiOverview,
    LegendComponentWrapper,
    LoadingSpinner,
    SerpCompositionMetricContainerStyled,
} from "./SerpCompositionStyledComponents";
import type { ISerpCompositionGraphItem } from "app/common/keyword-research/keywords-overview/serpCompositionTypes";
import { getValueAsPercentage } from "./serpCompositionUtils";

const DONUT_INNER_SIZE = "60%";
const LEGENDS_GRID_DIRECTION = "row";

interface ISerpComposition {
    isLoading: boolean;
    graphItems: ISerpCompositionGraphItem[];
    aiExistence: number;
}

interface IAiExistenceOverviewProps {
    aiExistence: number;
    isLoading: boolean;
    hideAioExistence: boolean;
    isKeywordGroup: boolean;
    keywordObject?: IUserKeywordGroup;
}

const AiExistenceOverview = ({
    aiExistence,
    isLoading,
    isKeywordGroup,
    keywordObject,
    hideAioExistence,
}: IAiExistenceOverviewProps) => {
    if (hideAioExistence || isLoading) {
        return null;
    }

    return (
        <AiOverview alignItems="center">
            <SWReactIcons
                size={"xs"}
                iconColor={colorsPalettes.torquoise[500]}
                iconName="spark-ai"
            />
            {aiExistence === 0 ? (
                <Text
                    fontVariant="subtitle2"
                    color={colorsPalettes.torquoise[500]}
                >
                    {translate("keyword.analysis.overview.page.v2.serp_composition.ai.title.not.exist")}
                </Text>
            ) : (
                <>
                    <Text
                        fontVariant="subtitle2"
                        color={colorsPalettes.torquoise[500]}
                    >
                        {isKeywordGroup
                            ? translate("keyword.analysis.overview.page.v2.serp_composition.ai.title")
                            : translate("keyword.analysis.overview.page.v2.serp_composition.ai.title.single")}
                    </Text>
                    {isKeywordGroup ? (
                        <Text
                            fontVariant="body2"
                            color={colorsPalettes.torquoise[500]}
                        >
                            {translate("keyword.analysis.overview.page.v2.serp_composition.ai.subtitle.group", {
                                count: aiExistence,
                                total: keywordObject?.Keywords?.length || 0,
                            })}
                        </Text>
                    ) : null}
                </>
            )}
        </AiOverview>
    );
};

export const SerpComposition: FC<ISerpComposition> = ({ isLoading, graphItems = [], aiExistence }) => {
    const hasNoData = !isLoading && graphItems.every((item) => !item.y);
    const { params } = useCurrentStateAndParams();
    const { keyword, country, graphDuration } = params;

    const keywordObject = useKeywordGroupById(keyword.slice(1));
    const isKeywordGroup = keywordGroupsTransforms.isKeywordGroup(keyword);

    const hideAioExistence = country === "999" || graphDuration === BasicDurations.LAST_TWENTY_EIGHT_DAYS;
    return (
        <SerpCompositionMetricContainerStyled
            height="300px"
            aiExistence={aiExistence >= 0 ? !hideAioExistence : null}
        >
            <BoxTitle
                Container={DataRowTitle}
                tooltip={translate("keyword.analysis.overview.page.v2.serp_composition.tooltip")}
            >
                <DataTitle>{translate("keyword.analysis.overview.page.v2.serp_composition.title")}</DataTitle>
            </BoxTitle>
            {!hideAioExistence ? (
                <AiExistenceOverview
                    aiExistence={aiExistence}
                    hideAioExistence={hideAioExistence}
                    isLoading={isLoading}
                    isKeywordGroup={isKeywordGroup}
                    keywordObject={keywordObject.data}
                />
            ) : null}
            {isLoading ? (
                <LoadingSpinner />
            ) : (
                <DonutChart
                    data={[{ data: graphItems }]}
                    config={getConfig(hasNoData)}
                    shouldPresentLegend={true}
                    legendsGridDirection={LEGENDS_GRID_DIRECTION}
                    shouldPresentValueAtLegend={true}
                    withoutTooltipsPadding={false}
                    legendPosition={LEGEND_POSITIONS.RIGHT}
                    legendsMaxWidth={250}
                    // @ts-expect-error FIXME: Type '(value: number, decimal?: number) => string' is not assignable to type '(value: number, data: PieDataItem) => string'
                    legendValueFilter={getValueAsPercentage}
                    innerSize={DONUT_INNER_SIZE}
                    customLegendComponentWrapper={LegendComponentWrapper}
                />
            )}
        </SerpCompositionMetricContainerStyled>
    );
};
